import React, { useMemo } from "react";
import { Stack, Text } from "@mantine/core";

import { Client } from "@/utils/api/ClientService";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  StatusBadge,
  RecordActions,
} from "@/components/generic";

export interface ClientsTableProps {
  /** Array of clients to display */
  clients: Client[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditClient?: (client: Client) => void;

  /** Callback when remove action is clicked */
  onRemoveClient?: (client: Client) => void;

  /** Callback when view action is clicked */
  onViewClient?: (client: Client) => void;

  /** Callback when go in action is clicked */
  onGoInClient?: (client: Client) => void;

  /** ID of client currently being removed */
  removingClientId?: string | null;

  /** ID of client just edited or added */
  editedItemId?: string | null | undefined;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const ClientsTable: React.FC<ClientsTableProps> = ({
  clients,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditClient,
  onRemoveClient,
  onViewClient,
  onGoInClient,
  removingClientId,
  editedItemId,
  size = "md",
}) => {
  // Helper function to format client name
  const formatClientName = (client: Client): string => {
    const parts: string[] = [];

    if (client.nameTitle) parts.push(client.nameTitle);
    if (client.firstName) parts.push(client.firstName);
    if (client.lastName) parts.push(client.lastName);

    return parts.length > 0 ? parts.join(" ") : "Unnamed Client";
  };

  // Helper function to get display name (company or person)
  const getDisplayName = (
    client: Client
  ): { primary: string; secondary?: string } => {
    if (client.companyName) {
      const personName = formatClientName(client);
      return {
        primary: client.companyName || personName,
        secondary: !client.companyName ? personName : "",
      };
    } else {
      return {
        primary: formatClientName(client),
      };
    }
  };

  // Define table columns
  const columns: AppTableColumn<Client>[] = useMemo(
    () => [
      {
        key: "companyName",
        label: "Client",
        sortable: true,
        flex: true,
        minWidth: 250,
        render: (_, record) => {
          const { primary, secondary } = getDisplayName(record);
          return (
            <Stack gap={0}>
              <Text fw={500}>{primary}</Text>
              {secondary && (
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {secondary}
                </Text>
              )}
            </Stack>
          );
        },
      },
      {
        key: "clientType",
        label: "Type",
        sortable: true,
        width: 120,
        minWidth: 120,
        render: (value) => {
          if (!value) return <Text c="dimmed">—</Text>;
          return (
            <Text size="sm" tt="capitalize">
              {value}
            </Text>
          );
        },
      },
      {
        key: "phone",
        label: "Contact",
        sortable: false,
        width: 200,
        minWidth: 200,
        //hideOnMobile: true,
        render: (_, record) => (
          <Stack gap={0}>
            {record.email && (
              <Text size="xs" c="dimmed">
                📧 {record.email}
              </Text>
            )}
            {record.phone && (
              <Text size="xs" c="dimmed">
                📞 {record.phone}
              </Text>
            )}
            {record.website && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                🌐 {record.website}
              </Text>
            )}
          </Stack>
        ),
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 120,
        align: "left",
        //hideOnMobile: true,
        render: (value) => {
          return <StatusBadge status={value} />;
        },
      },
      {
        key: "actions",
        label: "",
        width: 200,
        minWidth: 200,
        align: "left",
        render: (_, record) => (
          <RecordActions
            context={record}
            onViewClick={onViewClient}
            onEditClick={onEditClient}
            onRemoveClick={onRemoveClient}
            onGoInClick={onGoInClient}
            removeInProgress={removingClientId === record.id}
          />
        ),
      },
    ],
    [onViewClient, onEditClient, onRemoveClient, removingClientId]
  );

  return (
    <AppTable
      data={clients}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(client) => client?.id || ""}
      highlightRow={(client) => client?.id == editedItemId}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
